import {
  Avatar,
  ClickAwayListener,
  IconButton,
  Popper,
  styled,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ExpandMore } from '@mui/icons-material'
import { GridApi, IRowNode } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import { ProjectMemberSearchOption } from '../../../../domain/value-object/ProjectMemberSearchOption'
import { ReferencedEntityWithIcon } from '../../../../domain/value-object/ReferencedEntity'
import { intl } from '../../../../i18n'
import { filterValuesByCombination } from '../../../../lib/commons/customEnum'
import { getWbsItemStatusLabel } from '../../../../lib/functions/wbsItem'
import { useProjectMemberRepository } from '../../../../services/adaptors/projectMemberRepositoryAdaptor'
import objects from '../../../../utils/objects'
import DateVO from '../../../../vo/DateVO'
import ClearIcon from '../../../components/icons/ClearIcon'
import { CloseIcon } from '../../../components/icons/CloseIcon'
import DateSelect, {
  DateSelectType,
} from '../../../components/toolbars/common/DateSelect'
import { ProjectMemberInput } from '../../../features/ProjectMemberInput'
import { getOptionStyles } from '../../../features/WbsItemStatusChip'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../features/ProjectMemberPopper'
import { useDebounce } from '../../../hooks/useDebounce'
import { colorPalette } from '../../../style/colorPalette'
import { statusCombinedValuePath } from '../gridOptions/defaultColumns'
import {
  DEFAULT_GANTT_BAR_COLOR,
  GANTT_BAR_COLORS,
  GanttBarColorName,
  getBarColorByType,
  getInheritedGanttColor,
} from '../../../containers/commons/AgGrid/components/cell/custom/gantt/ganttUtil'
import { WbsItemEditPopperAnchor } from '../hooks/wbsItemEditPopper'
import { ProjectPlanNewRow } from '../projectPlanNew'

const FIELD_STATUS = 'body.wbsItem.status'
const FIELD_RESPONSIBLE = 'body.wbsItem.responsible'
const FIELD_SCHEDULED_START_DATE = 'body.wbsItem.scheduledDate.startDate'
const FIELD_SCHEDULED_END_DATE = 'body.wbsItem.scheduledDate.endDate'

const DROP_DOWN_CONTROL_ATTRIBUTE = 'data-drop-down-control'

const CLICK_AWAY_IGNORED_SELECTOR =
  '.MuiPopper-root, .MuiPopover-root, .react-datepicker, .react-datepicker-popper'

const DATE_SELECT_SX = { width: '130px' }

const POPPER_Z_INDEX = 1300

const POPPER_MODIFIERS = [{ name: 'offset', options: { offset: [0, 8] } }]

const STATUS_OPTION_LIST_Z_INDEX = 10

const STATUS_CHIP_MIN_WIDTH = '77px'
const STATUS_CHIP_HEIGHT = '22px'
const STATUS_CHIP_PADDING = '4px 6px 4px 8px'
const STATUS_CHIP_BORDER_RADIUS = '4px'
const STATUS_CHIP_FONT_FAMILY = 'Noto Sans JP'
const STATUS_CHIP_FONT_SIZE = '12px'
const STATUS_CHIP_FONT_WEIGHT = 500
const STATUS_CHIP_LINE_HEIGHT = '100%'
const STATUS_CHIP_SHADOW_ALPHA_HEX = '4D'
const STATUS_DROP_DOWN_LIST_WIDTH = '83px'
const STATUS_DROP_DOWN_OPTION_MARGIN = '3px'

type DropDownField = 'status' | 'responsible'

type WbsItemEditPopperProps = {
  anchorEl: WbsItemEditPopperAnchor
  rowNode: IRowNode<ProjectPlanNewRow>
  api: GridApi<ProjectPlanNewRow>
  onClose: () => void
  onChangeGanttColor: (wbsItemUuid: string, color: GanttBarColorName) => void
}

export const WbsItemEditPopper = ({
  anchorEl,
  rowNode,
  api,
  onClose,
  onChangeGanttColor,
}: WbsItemEditPopperProps) => {
  const wbsItem = rowNode.data?.body?.wbsItem
  const [status, setStatus] = useState<WbsItemStatus | undefined>(
    wbsItem?.status
  )
  const [responsible, setResponsible] = useState<
    ReferencedEntityWithIcon | undefined
  >(wbsItem?.responsible)
  const [startDate, setStartDate] = useState<string | undefined>(
    wbsItem?.scheduledDate?.startDate
  )
  const [endDate, setEndDate] = useState<string | undefined>(
    wbsItem?.scheduledDate?.endDate
  )
  // 自身が未設定でも親工程から色を継承しているため、表示中の色を初期選択にする
  const [color, setColor] = useState<GanttBarColorName>(
    getInheritedGanttColor(
      rowNode,
      api.getGridOption('context')?.['wbsItemGanttColors'],
      'body.wbsItem.uuid'
    ) ?? DEFAULT_GANTT_BAR_COLOR
  )
  const [openField, setOpenField] = useState<DropDownField | undefined>()
  const isProcess = !!wbsItem?.wbsItemType?.isProcess()

  const statusOptions = useMemo(() => {
    const options = api.getGridOption('context')?.['status']
    if (!options) return []
    return filterValuesByCombination(options, code =>
      objects.getValue(rowNode.data, statusCombinedValuePath(code))
    )
  }, [api, rowNode])

  const applyChange = useCallback(
    (field: string, value: unknown) => {
      rowNode.setDataValue(field, value)
      const updated = rowNode.data?.body?.wbsItem
      if (!updated) return
      setStatus(updated.status)
      setResponsible(updated.responsible)
      setStartDate(updated.scheduledDate?.startDate)
      setEndDate(updated.scheduledDate?.endDate)
    },
    [rowNode]
  )

  const closeDropDown = useCallback(() => setOpenField(undefined), [])

  const toggleDropDown = useCallback(
    (field: DropDownField) =>
      setOpenField(prev => (prev === field ? undefined : field)),
    []
  )

  const onChangeStatus = useCallback(
    (newValue: string) => {
      closeDropDown()
      applyChange(FIELD_STATUS, newValue)
    },
    [applyChange, closeDropDown]
  )

  const onChangeResponsible = useCallback(
    (member?: ProjectMemberSearchOption) => {
      closeDropDown()
      applyChange(FIELD_RESPONSIBLE, member)
    },
    [applyChange, closeDropDown]
  )

  const onChangeStartDate = useCallback(
    (value?: string) => applyChange(FIELD_SCHEDULED_START_DATE, value),
    [applyChange]
  )

  const onChangeEndDate = useCallback(
    (value?: string) => applyChange(FIELD_SCHEDULED_END_DATE, value),
    [applyChange]
  )

  // 色は WbsItem の項目ではなく別テーブルの表示設定なので、
  // 他項目の保存経路（applyChange/body.wbsItem）には乗せず専用APIで保存する。
  // ただし保存タイミングは他項目と揃え、ここでは変更を溜めるだけにする
  const onChangeColor = useCallback(
    (value: GanttBarColorName) => {
      setColor(value)
      const wbsItemUuid = rowNode.data?.body?.wbsItem?.uuid
      if (!wbsItemUuid) return
      // 既定色も明示的な設定として扱う。未設定は親工程からの継承を意味するため、
      // 既定色を選んだときに未設定へ戻すと親の色に戻ってしまい、継承を断ち切れない
      onChangeGanttColor(wbsItemUuid, value)
    },
    [rowNode, onChangeGanttColor]
  )

  const onClickInside = useCallback((event: React.MouseEvent) => {
    const target = event.target
    if (
      target instanceof Element &&
      target.closest(`[${DROP_DOWN_CONTROL_ATTRIBUTE}]`)
    ) {
      return
    }
    setOpenField(undefined)
  }, [])

  const onClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target
      if (
        target instanceof Element &&
        target.closest(CLICK_AWAY_IGNORED_SELECTOR)
      ) {
        return
      }
      onClose()
    },
    [onClose]
  )

  if (!wbsItem) return null

  return (
    <Popper
      open={true}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={POPPER_MODIFIERS}
      sx={{ zIndex: POPPER_Z_INDEX }}
    >
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={onClickAway}>
        <Root onClick={onClickInside}>
          <Header>
            <TypeIcon src={wbsItem.wbsItemType.iconUrl} />
            <TargetName>
              {wbsItem.displayName ||
                intl.formatMessage({ id: 'displayName.undefined' })}
            </TargetName>
            {wbsItem.code && <TargetCode>{wbsItem.code}</TargetCode>}
            <CloseIcon onClose={onClose} size="s" />
          </Header>
          <Row>
            <Label>{intl.formatMessage({ id: 'projectPlan.status' })}</Label>
            <Control>
              <StatusDropDownAnchor
                {...{ [DROP_DOWN_CONTROL_ATTRIBUTE]: 'status' }}
              >
                <StatusTrigger
                  status={status}
                  onClick={() => toggleDropDown('status')}
                />
                {openField === 'status' && (
                  <StatusOptionList>
                    {statusOptions.map(option => (
                      <StatusOption
                        key={option.value}
                        value={option.value}
                        onClick={() => onChangeStatus(option.value)}
                      />
                    ))}
                  </StatusOptionList>
                )}
              </StatusDropDownAnchor>
            </Control>
          </Row>
          <Row>
            <Label>
              {intl.formatMessage({ id: 'projectPlan.responsible' })}
            </Label>
            <Control {...{ [DROP_DOWN_CONTROL_ATTRIBUTE]: 'responsible' }}>
              <MemberSelect
                value={responsible}
                isOpen={openField === 'responsible'}
                onChange={onChangeResponsible}
                onOpen={() => toggleDropDown('responsible')}
                onCancel={closeDropDown}
              />
            </Control>
          </Row>
          <Row>
            <Label>
              {intl.formatMessage({ id: 'projectPlan.scheduledDate.start' })}
            </Label>
            <Control>
              <DateSelect
                type={DateSelectType.DATE}
                value={startDate ? new DateVO(startDate) : undefined}
                onChange={onChangeStartDate}
                showYearMonthDropdown={true}
                sx={DATE_SELECT_SX}
              />
            </Control>
          </Row>
          <Row>
            <Label>
              {intl.formatMessage({ id: 'projectPlan.scheduledDate.end' })}
            </Label>
            <Control>
              <DateSelect
                type={DateSelectType.DATE}
                value={endDate ? new DateVO(endDate) : undefined}
                onChange={onChangeEndDate}
                showYearMonthDropdown={true}
                sx={DATE_SELECT_SX}
              />
            </Control>
          </Row>
          {isProcess && (
            <Row>
              <Label>{intl.formatMessage({ id: 'projectPlan.color' })}</Label>
              <Control>
                <ColorOptionList>
                  {GANTT_BAR_COLORS.map(presetColor => (
                    <ColorOptionButton
                      key={presetColor}
                      type="button"
                      selected={color === presetColor}
                      onClick={() => onChangeColor(presetColor)}
                    >
                      <ColorCircle
                        bgcolor={getBarColorByType(
                          'process',
                          false,
                          presetColor
                        )}
                      />
                    </ColorOptionButton>
                  ))}
                </ColorOptionList>
              </Control>
            </Row>
          )}
        </Root>
      </ClickAwayListener>
    </Popper>
  )
}

type StatusTriggerProps = {
  status: WbsItemStatus | undefined
  onClick: () => void
}

const StatusTrigger = ({ status, onClick }: StatusTriggerProps) => {
  const { backgroundColor, textColor, borderColor } = getOptionStyles(
    status ?? ''
  )
  return (
    <StatusTriggerButton
      type="button"
      onClick={onClick}
      backgroundColor={backgroundColor}
      textColor={textColor}
      border={borderColor}
    >
      {status && getWbsItemStatusLabel(status)}
      <StatusExpandMoreIcon />
    </StatusTriggerButton>
  )
}

type StatusOptionProps = {
  value: string
  onClick: () => void
}

const StatusOption = ({ value, onClick }: StatusOptionProps) => {
  const { backgroundColor, textColor, borderColor } = getOptionStyles(value)
  return (
    <StatusOptionItem
      type="button"
      onClick={onClick}
      backgroundColor={backgroundColor}
      textColor={textColor}
      border={borderColor}
    >
      {getWbsItemStatusLabel(value as WbsItemStatus)}
    </StatusOptionItem>
  )
}

type MemberSelectProps = {
  value: ReferencedEntityWithIcon | undefined
  isOpen: boolean
  onChange: (member?: ProjectMemberSearchOption) => void
  onOpen: () => void
  onCancel: () => void
}

const MemberSelect = ({
  value,
  isOpen,
  onChange,
  onOpen,
  onCancel,
}: MemberSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const onClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      onChange(undefined)
    },
    [onChange]
  )

  return (
    <MemberSelectRoot ref={containerRef}>
      {isOpen ? (
        <MemberSearch
          anchorEl={containerRef.current}
          onChange={onChange}
          onCancel={onCancel}
        />
      ) : (
        <InputFrame onClick={onOpen}>
          <SelectedMember>
            {value ? (
              <>
                <MemberAvatar src={value.iconUrl ?? undefined} />
                <MemberName>{value.name}</MemberName>
              </>
            ) : (
              <UnselectedLabel>
                {intl.formatMessage({ id: 'wbsItem.unassigned' })}
              </UnselectedLabel>
            )}
            {value && (
              <ClearIconButton onClick={onClear}>
                <ClearIcon />
              </ClearIconButton>
            )}
            <ExpandMoreIcon />
          </SelectedMember>
        </InputFrame>
      )}
    </MemberSelectRoot>
  )
}

type MemberSearchProps = {
  anchorEl: HTMLDivElement | null
  onChange: (member?: ProjectMemberSearchOption) => void
  onCancel: () => void
}

const MemberSearch = ({ anchorEl, onChange, onCancel }: MemberSearchProps) => {
  const [searchText, setSearchText] = useState<string>('')
  const [options, setOptions] = useState<ProjectMemberSearchOption[]>([])
  const debouncedSearchText = useDebounce(searchText, 300) ?? ''
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)
  const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    let isMounted = true
    const fn = async () => {
      const response =
        await searchAsProjectMemberSearchOption(debouncedSearchText)
      if (isMounted) {
        setOptions(response)
      }
    }
    fn()
    return () => {
      isMounted = false
    }
  }, [searchAsProjectMemberSearchOption, debouncedSearchText])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        popperRef.current?.enter()
      } else if (event.key === 'Escape') {
        event.stopPropagation()
        onCancel()
      }
    },
    [onCancel]
  )

  return (
    <>
      <InputFrame>
        <ProjectMemberInput
          inputRef={inputRef}
          value={searchText}
          onChange={setSearchText}
          onKeyDown={onKeyDown}
        />
      </InputFrame>
      <ProjectMemberPopper
        ref={popperRef}
        open={true}
        anchorEl={anchorEl}
        options={options}
        onSelect={onChange}
        inputRef={inputRef}
        searchText={searchText}
      />
    </>
  )
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '300px',
  padding: '12px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  boxShadow: `0px 2px 8px 0px ${colorPalette.monotone[4]}80`,
})

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  paddingBottom: '8px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const TypeIcon = styled('img')({
  height: '16px',
  width: '16px',
  flexShrink: 0,
})

const TargetName = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '12px',
  fontWeight: 500,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexGrow: 1,
})

const TargetCode = styled('span')({
  color: colorPalette.monotone[4],
  fontSize: '11px',
  flexShrink: 0,
})

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const Label = styled('span')({
  color: colorPalette.monotone[5],
  fontSize: '12px',
  flexShrink: 0,
  width: '84px',
})

const Control = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexGrow: 1,
  minWidth: 0,
})

const ColorCircle = styled('div')<{ bgcolor: string }>(({ bgcolor }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  backgroundColor: bgcolor,
}))

const ColorOptionList = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
})

const ColorOptionButton = styled('button', {
  shouldForwardProp: prop => prop !== 'selected',
})<{ selected?: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  padding: 0,
  border: `1px solid ${selected ? colorPalette.skyBlue[7] : 'transparent'}`,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  cursor: 'pointer',
}))

const InputFrame = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: '28px',
  padding: '2px 8px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: colorPalette.monotone[3],
  },
  '& .MuiInputBase-input': {
    color: colorPalette.monotone[10],
    fontSize: STATUS_CHIP_FONT_SIZE,
    padding: 0,
  },
})

const StatusDropDownAnchor = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  position: 'relative',
})

const StatusOptionList = styled('div')({
  position: 'absolute',
  top: 'calc(100% + 2px)',
  left: 0,
  zIndex: STATUS_OPTION_LIST_Z_INDEX,
  display: 'flex',
  flexDirection: 'column',
  width: STATUS_DROP_DOWN_LIST_WIDTH,
  padding: '3px 0',
  borderRadius: '4px',
  backgroundColor: colorPalette.monotone[0],
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
})

const StatusOptionItem = styled('button', {
  shouldForwardProp: prop =>
    prop !== 'backgroundColor' && prop !== 'textColor' && prop !== 'border',
})<{ backgroundColor: string; textColor: string; border: string }>(
  ({ backgroundColor, textColor, border }) => ({
    margin: STATUS_DROP_DOWN_OPTION_MARGIN,
    display: 'flex',
    width: STATUS_CHIP_MIN_WIDTH,
    height: STATUS_CHIP_HEIGHT,
    padding: STATUS_CHIP_PADDING,
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: STATUS_CHIP_FONT_FAMILY,
    fontSize: STATUS_CHIP_FONT_SIZE,
    fontWeight: STATUS_CHIP_FONT_WEIGHT,
    lineHeight: STATUS_CHIP_LINE_HEIGHT,
    borderRadius: STATUS_CHIP_BORDER_RADIUS,
    cursor: 'pointer',
    backgroundColor,
    color: textColor,
    border,
    '&:hover': {
      backgroundColor: colorPalette.monotone[2],
      border: `1px solid ${colorPalette.monotone[2]}`,
    },
  })
)

const StatusTriggerButton = styled('button', {
  shouldForwardProp: prop =>
    prop !== 'backgroundColor' && prop !== 'textColor' && prop !== 'border',
})<{ backgroundColor: string; textColor: string; border: string }>(
  ({ backgroundColor, textColor, border }) => ({
    display: 'flex',
    minWidth: STATUS_CHIP_MIN_WIDTH,
    height: STATUS_CHIP_HEIGHT,
    padding: STATUS_CHIP_PADDING,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0,
    borderRadius: STATUS_CHIP_BORDER_RADIUS,
    fontFamily: STATUS_CHIP_FONT_FAMILY,
    fontSize: STATUS_CHIP_FONT_SIZE,
    fontWeight: STATUS_CHIP_FONT_WEIGHT,
    lineHeight: STATUS_CHIP_LINE_HEIGHT,
    cursor: 'pointer',
    backgroundColor,
    color: textColor,
    border,
    boxShadow: `0px 1px 2px 0px ${colorPalette.monotone[4]}${STATUS_CHIP_SHADOW_ALPHA_HEX}`,
  })
)

const StatusExpandMoreIcon = styled(ExpandMore)({
  color: colorPalette.monotone[4],
  height: '14px',
  width: '14px',
  flexShrink: 0,
})

const MemberSelectRoot = styled('div')({
  position: 'relative',
  width: '100%',
})

const SelectedMember = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '100%',
})

const MemberAvatar = styled(Avatar)({
  height: '20px',
  width: '20px',
})

const MemberName = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexGrow: 1,
})

const UnselectedLabel = styled('span')({
  color: colorPalette.monotone[4],
  fontSize: '12px',
  flexGrow: 1,
})

const ClearIconButton = styled(IconButton)({
  padding: '2px',
})

const ExpandMoreIcon = styled(ExpandMoreRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '16px',
  width: '16px',
})
